import { type FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClaimAllResourcesMutation } from '@api/queries';
import { getImage } from '@common/lib/images';
import { SendAnalitics } from '@common/lib/playdeck';
import { AdsContext } from '@components/Ads/context';
import { ShortStorageItem } from '@components/ShortStorageItem/ShortStorageItem';
import {
  H1,
  StringMain,
  StringSmall,
  StringXSmall,
  TextSmall,
} from '@components/Typography/Typography';
import { LockResource } from '@components/ui/LockResource/LockResource';
import { addToBalance } from '@redux/reducers/userReducer';
import {
  collectBonus,
  selectAllResourcesForClaim,
} from '@redux/selectors/quarrySelectors';
import { useAppDispatch, useAppSelector } from '@redux/store';
import cn from 'classnames';

import { PICTURES_CONFIG } from '@/common/config/media-config';
import { resourceLevels } from '@/common/constants/levels';
import { useCheckVIPstatus } from '@/common/hooks/checkVIPstatus';
import { playSound } from '@/common/lib/soundEffect';
import { Ticker } from '@/components/common/Ticker/Ticker';
import { MainButton, PurpleButton } from '@/components/ui/Button/Button';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { Picture } from '@/components/ui/Picture/Picture';

import { ModalContext } from '../Modal/config';

import css from './styles.module.scss';

import tgemPng from '/img/tgem.png';
import tgemWebp from '/img/tgem.webp';
import vipCrown from '/img/VIP/vip.svg';

const claimTypes = ['free', 'ads', 't-gem'] as const;
const claimTypesVip = ['free', 'vip', 't-gem'] as const;

type ClaimType = (typeof claimTypes)[number] | (typeof claimTypesVip)[number];

const claimData: Record<ClaimType, { label: string; value: number }> = {
  free: {
    label: 'FREE',
    value: 0,
  },
  ads: {
    label: 'ADS',
    value: 10,
  },
  't-gem': {
    label: 'T-Gem',
    value: 50,
  },
  vip: {
    label: 'VIP',
    value: 10,
  },
};

const WelcomeBackModal: FC = () => {
  const { t } = useTranslation();
  const { showAd, adsIsLoading } = useContext(AdsContext);
  const statusVIP = useCheckVIPstatus();
  const coinBalance = useAppSelector((state) => state.user.user?.balance.tgem);
  const resourceListForClaim = useAppSelector(selectAllResourcesForClaim);
  const { closeModal, secondModal } = useContext(ModalContext);
  const [claimResources, { isLoading }] = useClaimAllResourcesMutation();
  const [selectedClaimType, setSelectedClaimType] = useState<ClaimType>(
    statusVIP ? 'vip' : 'free',
  );
  // const checkStockOverflow = useCheckStorageOverflow();
  const [bonus, setBonus] = useState<number | undefined>();
  const collectPrice = useAppSelector(collectBonus);
  const dispatch = useAppDispatch();
  const handleClaim = (requestBody: {
    adsView: boolean;
    paid: boolean;
  }): void => {
    playSound('buttons/switch');
    claimResources(requestBody)
      .unwrap()
      .then(() => {
        if (requestBody.paid) {
          SendAnalitics.coinSpend({
            amount: collectPrice || 0,
            type: 'collect_resources',
            item_name: 'resources',
          });
        }
        sessionStorage.setItem('isAllResourcesClaimed', 'true');
        // checkStockOverflow(storage, gameItemOrder.resource);
        closeModal();
      });
  };

  const handleWatchAds = (): void => {
    showAd({
      placements: 'welcomeback',
      onReward: () => {
        handleClaim({ adsView: true, paid: false });
      },
      onError: () => {
        showToast(t('Ads launch error'));
      },
    });
  };

  const buyCollectBonus = (): void => {
    if ((coinBalance || 0) >= (collectPrice || 0)) {
      dispatch(addToBalance({ tgem: (collectPrice || 0) * -1 }));
      handleClaim({ adsView: false, paid: true });
    } else {
      secondModal.openModal({
        renderKind: 'coinDeposit',
        props: { source: 'welcome_back_modal' },
      });
    }
  };

  const generalClick = (type: ClaimType): void => {
    switch (type) {
      case 'free':
        handleClaim({ adsView: false, paid: false });
        break;
      case 'vip':
        handleClaim({ adsView: true, paid: false });
        break;
      case 'ads':
        handleWatchAds();
        break;
      case 't-gem':
        buyCollectBonus();
        break;
    }
  };

  return (
    <div className={cn('w-full relative z-10')}>
      <Picture
        img={PICTURES_CONFIG.LUMBERJACK_CHAR_SMALL.png}
        webp={PICTURES_CONFIG.LUMBERJACK_CHAR_SMALL.webp}
        alt="lumberjack"
        className={css.character}
      />
      <div className="w-full flex flex-col items-center px-4 gap-4">
        <div className="h-20 w-full top-0 left-0 absolute z-[-1] from-white/10 to-white/0 bg-gradient-to-b" />
        <div className="flex flex-col items-center gap-2 relative pt-4">
          <H1 weight="bold">{t('Welcome back!')}</H1>
          <TextSmall className="!leading-[1.2] opacity-60 max-w-[max(15.75rem,_252px)] text-center">
            {t('While you were away, resources accumulated in the storage')}
          </TextSmall>
        </div>
        <div className={css.gridResources}>
          {resourceListForClaim?.map((props, index) => {
            const image = getImage({ name: props.kind, field: 'icon' });
            const minLevel = resourceLevels[props.kind];

            return props.storage ? (
              <ShortStorageItem key={props.kind} {...props} bonus={bonus} />
            ) : (
              <LockResource key={index} image={image} minLevel={minLevel} />
            );
          })}
        </div>

        <div className="grid grid-cols-[1fr_auto_1fr] gap-4 self-stretch items-center">
          <div className="border-t border-white/40" />
          <StringMain weight="bold">{t('Choose a claim')}</StringMain>
          <div className="border-t border-white/40" />
        </div>

        <div className="grid grid-cols-3 self-stretch gap-1 h-[5.5rem]">
          {(statusVIP ? claimTypesVip : claimTypes).map((type) => {
            const isCurrent = selectedClaimType === type;
            const isLast = claimTypes[claimTypes.length - 1] === type;
            const isDisabled = statusVIP && type === 'free';

            return (
              <button
                key={type}
                className={cn(
                  'p-3 rounded-lg bg-white/10 text-white justify-between border transition-all duration-75 flex flex-col items-start relative',
                  isCurrent
                    ? 'border-[#82E024] bg-[#82E024]/10'
                    : 'border-transparent',
                  isDisabled && 'opacity-50',
                )}
                disabled={isDisabled}
                onClick={() => {
                  playSound('buttons/switch');
                  setBonus(claimData[type].value);

                  setSelectedClaimType(type);
                }}
              >
                {isLast && (
                  <Ticker
                    variant="gold-small"
                    className="top-[-5px] right-[-5px] !absolute"
                    text="TOP"
                  />
                )}
                {type === 'vip' && (
                  <img
                    src={vipCrown}
                    className="size-4 absolute top-2 right-2"
                    alt="vip crown"
                  />
                )}

                <StringSmall
                  weight="bold"
                  className={cn(isCurrent && '!text-[#82E024]')}
                >
                  {claimData[type].label}
                </StringSmall>
                <StringMain weight="extrabold">
                  +{claimData[type].value}%
                </StringMain>
              </button>
            );
          })}
        </div>

        {selectedClaimType === 't-gem' ? (
          <PurpleButton
            onClick={() => generalClick(selectedClaimType)}
            disabled={isLoading || adsIsLoading}
            isLoading={isLoading || adsIsLoading}
          >
            <div className="flex items-center text-xl font-bold">
              {collectPrice}
              <Picture
                alt="tgem"
                img={tgemPng}
                webp={tgemWebp}
                className="size-6 min-w-6"
              />
            </div>
          </PurpleButton>
        ) : (
          <MainButton
            text={
              selectedClaimType === 'vip' ? t('Premium Collect') : t('Collect')
            }
            onClick={() => generalClick(selectedClaimType)}
            disabled={isLoading || adsIsLoading}
            isLoading={isLoading || adsIsLoading}
          />
        )}
        <StringXSmall className="text-center   !leading-[120%] text-white/60 max-w-[19rem]">
          {t(
            'Regularly clear the storage and use the accumulated resources for development and new achievements',
          )}
        </StringXSmall>
      </div>
    </div>
  );
};

export { WelcomeBackModal };
